import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaBookOpen, FaCalculator } from 'react-icons/fa';
import { IoIosArrowDropdownCircle, IoIosHome } from 'react-icons/io';
import { PiCopy, PiToolbox } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useMatch } from 'react-router-dom';

import { Popover, Tooltip } from 'antd';
import OpterraPhoenixLogo from 'assets/images/OpterraPhoenixLogo.png';
import {
  CALCULATORS_NAV_DESCRIPTION,
  CALCULATORS_NAV_LABEL,
  CALCULATORS_PAGE_PATH,
  FARMS_PAGE_PATH,
  GC_EDITOR_ROLE,
  GLOBAL_CATALOGUE_PATH,
  HOME_PAGE_PATH,
} from 'constants/Constant';
import {
  COPY_TOOL_MENU_DESCRIPTION,
  COPY_TOOL_MENU_LABEL,
  NAV_RAIL_CATALOGUE_DESCRIPTION,
  NAV_RAIL_CATALOGUE_LABEL,
  NAV_RAIL_EXPAND_SIDEBAR_ARIA_LABEL,
  NAV_RAIL_HOME_DESCRIPTION,
  NAV_RAIL_HOME_LABEL,
  NAV_RAIL_LOGO_ALT,
  NAV_RAIL_SIDEBAR_DESCRIPTION,
  NAV_RAIL_SIDEBAR_LABEL,
  NAV_RAIL_TOOLS_LABEL,
} from 'constants/uiConstants';
import { useAuth } from 'context/AuthContext';
import { hasUserRole } from 'utils/AuthUtils';
import MergeClassNames from 'utils/MergeClassNames';

import { getActiveOrganisationId } from '../../../redux/selectors/ActiveOrganisationSelectors';
import { getSidebarCollapsed } from '../../../redux/selectors/FarmSelectors';
import { openCopyTool } from '../../../redux/slices/CopyToolSlice';
import { setSidebarCollapsed } from '../../../redux/slices/FarmSlice';

interface ILeftNavRailProps {
  mobileMenuOpen?: boolean;
  onClose?: () => void;
}

const NavTooltipContent = ({
  label,
  description,
}: {
  label: string;
  description: string;
}) => (
  <>
    {label}
    <br />
    <span className="opacity-75">{description}</span>
  </>
);

const NavItem = ({
  to,
  label,
  description,
  icon,
  onClick,
  className,
  iconClassName,
}: {
  to: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  className?: string;
  iconClassName?: string;
}) => (
  <Tooltip
    title={<NavTooltipContent label={label} description={description} />}
    placement="right"
  >
    <NavLink to={to} onClick={onClick}>
      {({ isActive }) => (
        <div
          className={MergeClassNames(
            'flex flex-col items-center justify-center',
            className,
            isActive ? 'text-success' : 'text-gray-dark hover:text-success'
          )}
        >
          <div
            className={MergeClassNames(
              'flex h-7 items-center justify-center p-1',
              iconClassName,
              isActive &&
                'mb-1 rounded-md border border-b-4 border-success bg-white'
            )}
          >
            {icon}
          </div>
          <span className="text-center text-xs">{label}</span>
        </div>
      )}
    </NavLink>
  </Tooltip>
);

const TOOLBOX_POPOVER_CLASS = 'toolbox-popover';

/**
 * Bottom-of-rail toolbox. Opens on hover; a click (touch, keyboard) only ever
 * opens it, so the click after a hover doesn't toggle it shut, and moves focus
 * into the menu, which renders in a portal Tab can't reach. Esc closes it.
 */
const ToolboxMenu = ({ onOpenCopyTool }: { onOpenCopyTool: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const shouldFocusMenu = useRef(false);
  const menuRef = useRef<HTMLUListElement>(null);

  /** Deferred a tick: the menu's ref runs before antd's portal is on the page, and a detached element can't take focus. */
  const focusMenuSoon = useCallback(() => {
    window.setTimeout(() => {
      const item = firstItemRef.current;
      if (!shouldFocusMenu.current || !item?.isConnected) return;
      shouldFocusMenu.current = false;
      item.focus();
    });
  }, []);

  // First open: the menu mounts after the click.
  const setFirstItem = useCallback(
    (item: HTMLButtonElement | null) => {
      firstItemRef.current = item;
      if (item && shouldFocusMenu.current) focusMenuSoon();
    },
    [focusMenuSoon]
  );

  // Reopening: the menu is already mounted.
  useEffect(() => {
    if (isOpen && shouldFocusMenu.current && firstItemRef.current) {
      focusMenuSoon();
    }
  }, [isOpen, focusMenuSoon]);

  const handleTriggerClick = () => {
    if (isOpen) {
      firstItemRef.current?.focus();
      return;
    }
    shouldFocusMenu.current = true;
    setIsOpen(true);
  };

  /** However it closes, drops a pending focus and returns focus from the hidden menu to the trigger. */
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) return;
    shouldFocusMenu.current = false;
    if (menuRef.current?.contains(document.activeElement)) {
      triggerRef.current?.focus();
    }
  }, []);

  // The whole popover counts, including antd's padding around the menu.
  const isInsideToolbox = useCallback(
    (target: EventTarget | null) =>
      target instanceof Node &&
      (!!triggerRef.current?.contains(target) ||
        !!menuRef.current?.contains(target) ||
        (target instanceof Element &&
          !!target.closest(`.${TOOLBOX_POPOVER_CLASS}`))),
    []
  );

  // Tab away closes it; no next target (a Safari button click) keeps it open for the click.
  const handleBlur = (event: React.FocusEvent) => {
    if (event.relatedTarget && !isInsideToolbox(event.relatedTarget)) {
      handleOpenChange(false);
    }
  };

  // Touch has no hover-out, so a tap outside closes it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      if (!isInsideToolbox(event.target)) handleOpenChange(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen, handleOpenChange, isInsideToolbox]);

  const handleOpenCopyTool = () => {
    handleOpenChange(false);
    onOpenCopyTool();
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger="hover"
      classNames={{ root: TOOLBOX_POPOVER_CLASS }}
      placement="rightBottom"
      arrow={false}
      content={
        <ul
          ref={menuRef}
          onBlur={handleBlur}
          aria-label={NAV_RAIL_TOOLS_LABEL}
          className="flex flex-col"
          onKeyDown={(event) => {
            if (event.key === 'Escape') handleOpenChange(false);
          }}
        >
          <li>
            <button
              ref={setFirstItem}
              type="button"
              className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-rowHover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              onClick={handleOpenCopyTool}
            >
              <PiCopy
                size={18}
                className="mt-0.5 flex-shrink-0 text-gray-dark"
              />
              <span className="flex flex-col">
                <span className="text-sm font-semibold text-charcoal">
                  {COPY_TOOL_MENU_LABEL}
                </span>
                <span className="text-xs text-gray-muted">
                  {COPY_TOOL_MENU_DESCRIPTION}
                </span>
              </span>
            </button>
          </li>
        </ul>
      }
    >
      <button
        ref={triggerRef}
        onBlur={handleBlur}
        type="button"
        aria-expanded={isOpen}
        onClick={handleTriggerClick}
        onKeyDown={(event) => {
          if (event.key === 'Escape') handleOpenChange(false);
        }}
        className={MergeClassNames(
          'flex flex-col items-center justify-center hover:text-success',
          isOpen ? 'text-success' : 'text-gray-dark'
        )}
      >
        <div className="flex h-7 items-center justify-center p-1">
          <PiToolbox size={22} />
        </div>
        <span className="text-center text-xs">{NAV_RAIL_TOOLS_LABEL}</span>
      </button>
    </Popover>
  );
};

const LeftNavRail: React.FC<ILeftNavRailProps> = ({
  mobileMenuOpen = false,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const showCatalogue = hasUserRole(user, GC_EDITOR_ROLE);
  const sidebarCollapsed = useSelector(getSidebarCollapsed);
  const activeOrganisationId = useSelector(getActiveOrganisationId);
  const isFarmsPage = !!useMatch({ path: FARMS_PAGE_PATH, end: false });

  const handleOpenCopyTool = () => {
    onClose?.();
    dispatch(openCopyTool());
  };

  return (
    <>
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 md:hidden"
          onClick={onClose}
        />
      )}

      <nav
        className={MergeClassNames(
          'fixed left-0 top-0 z-20 flex h-full flex-col items-center bg-gray-bright py-4 transition-transform duration-300',
          'md:w-16 md:translate-x-0',
          mobileMenuOpen ? 'w-16 translate-x-0' : 'w-16 -translate-x-full',
          // Collapsed LH tree panel: the rail becomes the panel's right edge,
          // so it picks up the border that edge would otherwise be missing.
          isFarmsPage && sidebarCollapsed && 'border-r border-secondary'
        )}
      >
        <NavLink
          to={HOME_PAGE_PATH}
          className="mb-3 flex items-center justify-center"
          onClick={onClose}
        >
          <img
            src={OpterraPhoenixLogo}
            alt={NAV_RAIL_LOGO_ALT}
            className="h-8 w-auto"
          />
        </NavLink>

        {isFarmsPage && sidebarCollapsed && (
          <Tooltip
            title={
              <NavTooltipContent
                label={NAV_RAIL_SIDEBAR_LABEL}
                description={NAV_RAIL_SIDEBAR_DESCRIPTION}
              />
            }
            placement="right"
          >
            <button
              type="button"
              aria-label={NAV_RAIL_EXPAND_SIDEBAR_ARIA_LABEL}
              className="mb-2 flex flex-col items-center justify-center text-gray-dark hover:opacity-75"
              onClick={() => dispatch(setSidebarCollapsed(false))}
            >
              <div className="p-1">
                {/* Same chevron used to collapse the panel, mirrored to show
                    the opposite (expand) direction. */}
                <IoIosArrowDropdownCircle
                  size={18}
                  className="-rotate-90 transition-transform duration-150 hover:scale-[2]"
                />
              </div>
              <span className="text-center text-xs">
                {NAV_RAIL_SIDEBAR_LABEL}
              </span>
            </button>
          </Tooltip>
        )}

        <div className="flex flex-col items-center space-y-2">
          <NavItem
            to={FARMS_PAGE_PATH}
            label={NAV_RAIL_HOME_LABEL}
            description={NAV_RAIL_HOME_DESCRIPTION}
            icon={<IoIosHome size={22} />}
            onClick={onClose}
          />

          {/* Every user (OP-2585) — no role check. */}
          <NavItem
            to={CALCULATORS_PAGE_PATH}
            label={CALCULATORS_NAV_LABEL}
            description={CALCULATORS_NAV_DESCRIPTION}
            icon={<FaCalculator size={20} />}
            onClick={onClose}
          />

          {showCatalogue && (
            <NavItem
              to={GLOBAL_CATALOGUE_PATH}
              label={NAV_RAIL_CATALOGUE_LABEL}
              description={NAV_RAIL_CATALOGUE_DESCRIPTION}
              icon={<FaBookOpen size={22} />}
              onClick={onClose}
            />
          )}
        </div>

        {activeOrganisationId && (
          <div className="mt-auto">
            <ToolboxMenu onOpenCopyTool={handleOpenCopyTool} />
          </div>
        )}
      </nav>
    </>
  );
};

export default LeftNavRail;
